import React from 'react';
import {
  FaTasks, FaCheckCircle, FaChevronDown, FaChevronRight, FaFileAlt,
  FaFileInvoiceDollar, FaBriefcase, FaExternalLinkAlt, FaPlus, FaInfoCircle,
} from 'react-icons/fa';
import { ProjectMilestone, MilestoneJob } from '../../operations/services/PlantafelService';
import { getJobStatusLabel, getJobStatusColors, getMilestoneStatusInfo } from './projectDetailUtils';
import styles from './ProjectDetailPage.module.css';

interface ProjectMilestonesTabProps {
  milestones: ProjectMilestone[];
  milestonesLoading: boolean;
  milestonesOfferId: number | null;
  expandedMilestones: Set<number>;
  draggedJobId: number | null;
  dragOverMilestoneId: number | 'unassigned' | null;
  reassigningJobId: number | null;
  openingInvoiceMilestoneId: number | null;
  onToggleMilestoneExpand: (milestoneId: number) => void;
  onOpenMilestoneJobInPlantafel: (job: MilestoneJob) => void;
  onSetInvoiceMilestone: (milestone: ProjectMilestone) => void;
  onSetInvoiceConfirmOpen: (open: boolean) => void;
  onOpenExistingInvoice: (milestone: ProjectMilestone) => void;
  onSetGenerateJobsMilestone: (milestone: ProjectMilestone) => void;
  onSetGenerateJobsConfirmOpen: (open: boolean) => void;
  onJobDragStart: (e: React.DragEvent, jobId: number) => void;
  onJobDragEnd: () => void;
  onMilestoneJobDragOver: (e: React.DragEvent, milestoneId: number | 'unassigned') => void;
  onMilestoneJobDragLeave: () => void;
  onMilestoneJobDrop: (e: React.DragEvent, targetMilestoneId: number | null) => void;
  onJobMilestoneChange: (jobId: number | undefined, milestoneId: number | null) => void;
}

export function ProjectMilestonesTab(props: ProjectMilestonesTabProps): React.JSX.Element {
  const { milestones, milestonesLoading, dragOverMilestoneId, onMilestoneJobDragOver, onMilestoneJobDragLeave, onMilestoneJobDrop } = props;
  return (
    <div data-testid="milestones-tab-content">
      <div className={styles.tabHeader}>
        <h3 className={styles.tabTitle}>Zahlungsplan / Milestones ({milestones.length})</h3>
      </div>
      {milestonesLoading ? (
        <div className={styles.loadingState}>Laden...</div>
      ) : milestones.length === 0 ? (
        <div className={styles.emptyState}>
          <FaTasks size={40} color="#ccc" className={styles.emptyStateIcon} />
          <p className={styles.emptyText}>Keine Milestones für dieses Projekt definiert</p>
        </div>
      ) : (
        <div data-testid="milestones-list" className={styles.milestonesList}>
          {milestones.map((m) => <MilestoneCard key={m.id} milestone={m} {...props} isExpanded={props.expandedMilestones.has(m.id)} />)}
          <div data-testid="milestone-job-dropzone-unassigned" onDragOver={(e) => onMilestoneJobDragOver(e, 'unassigned')} onDragLeave={onMilestoneJobDragLeave} onDrop={(e) => onMilestoneJobDrop(e, null)} className={styles.unassignedDropzone} style={{ background: dragOverMilestoneId === 'unassigned' ? '#fef2f2' : '#fafafa', border: dragOverMilestoneId === 'unassigned' ? '2px dashed #dc2626' : '2px dashed #e0e3ef' }}>
            <div className={styles.unassignedDropzoneInner}>
              <FaInfoCircle size={14} color={dragOverMilestoneId === 'unassigned' ? '#dc2626' : '#888'} />
              <span className={styles.unassignedDropzoneText}>Jobs hierher ziehen um Milestone-Zuordnung zu entfernen</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Single milestone card ── */

function MilestoneCard({ milestone, isExpanded, ...p }: ProjectMilestonesTabProps & { milestone: ProjectMilestone; isExpanded: boolean }): React.JSX.Element {
  const { milestones, milestonesOfferId, draggedJobId, dragOverMilestoneId, reassigningJobId, openingInvoiceMilestoneId } = p;
  const statusInfo = getMilestoneStatusInfo(milestone.status);
  const pctDone = milestone.computed?.percent_done || 0;
  const tTotal = milestone.computed?.tasks_total || 0;
  const tDone = milestone.computed?.tasks_done || 0;
  const isReady = milestone.status === 'READY_FOR_INVOICE';
  const isInvoicedOrPaid = milestone.status === 'INVOICED' || milestone.status === 'PAID';
  const canInvoice = !!milestonesOfferId;
  const canOpen = canInvoice && openingInvoiceMilestoneId !== milestone.id;

  const eurFmt = (v: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);

  return (
    <div data-testid={`milestone-card-${milestone.id}`} data-milestone-status={milestone.status} className={styles.milestoneCard}>
      {isReady && (
        <div data-testid={`milestone-ready-banner-${milestone.id}`} className={styles.milestoneBanner}>
          <div className={styles.milestoneBannerLeft}><FaCheckCircle size={14} color="#666" /> Bereit zur Rechnung</div>
          <button data-testid={`create-invoice-btn-${milestone.id}`} onClick={() => { p.onSetInvoiceMilestone(milestone); p.onSetInvoiceConfirmOpen(true); }} disabled={!canInvoice} className={styles.milestoneInvoiceButton} style={{ background: canInvoice ? '#ffffff' : '#f0f0f0', color: canInvoice ? '#021a4a' : '#888', cursor: canInvoice ? 'pointer' : 'not-allowed', opacity: canInvoice ? 1 : 0.7 }}>
            Rechnung erstellen
          </button>
        </div>
      )}
      {isInvoicedOrPaid && (
        <div data-testid={`milestone-invoiced-banner-${milestone.id}`} className={styles.milestoneBanner}>
          <div className={styles.milestoneBannerLeft}><FaFileInvoiceDollar size={14} color="#666" /> {milestone.status === 'PAID' ? 'Bezahlt' : 'Rechnung erstellt'}</div>
          <button data-testid={`open-invoice-btn-${milestone.id}`} onClick={() => p.onOpenExistingInvoice(milestone)} disabled={!canOpen} className={styles.milestoneInvoiceButton} style={{ background: canOpen ? '#ffffff' : '#f0f0f0', color: canOpen ? '#021a4a' : '#888', cursor: canOpen ? 'pointer' : 'not-allowed', opacity: canOpen ? 1 : 0.7 }}>
            {openingInvoiceMilestoneId === milestone.id ? 'Öffne...' : 'Rechnung öffnen'}
          </button>
        </div>
      )}
      <div data-testid={`milestone-job-dropzone-${milestone.id}`} onDragOver={(e) => p.onMilestoneJobDragOver(e, milestone.id)} onDragLeave={p.onMilestoneJobDragLeave} onDrop={(e) => p.onMilestoneJobDrop(e, milestone.id)} className={styles.milestoneContent} style={{ border: dragOverMilestoneId === milestone.id ? '2px dashed #7c3aed' : '2px dashed transparent', background: dragOverMilestoneId === milestone.id ? '#f5f3ff' : 'transparent', transition: 'all 0.15s' }}>
        <div className={styles.milestoneHeaderRow}>
          <div>
            <h4 className={styles.milestoneLabel}>{milestone.label}</h4>
            <div className={styles.milestoneMeta}>{milestone.percent}% des Projektwertes{milestone.amount && ` • ${eurFmt(milestone.amount)}`}</div>
          </div>
          <span data-testid={`milestone-status-badge-${milestone.id}`} className={styles.milestoneStatusBadge}>{statusInfo.label}</span>
        </div>
        <div data-testid={`milestone-progress-${milestone.id}`} className={styles.progressMargin}>
          <div className={styles.progressRow}>
            <span className={styles.progressLabel}>Fortschritt: {tDone} / {tTotal} Tasks</span>
            <span data-testid={`milestone-percent-${milestone.id}`} className={styles.progressPercent}>{pctDone.toFixed(0)}%</span>
          </div>
          <div className={styles.progressBarBg}>
            <div data-testid={`milestone-progress-bar-${milestone.id}`} data-progress={pctDone} className={styles.progressBarFill} style={{ width: `${pctDone}%` }} />
          </div>
        </div>

        {/* Jobs */}
        {milestone.jobs && milestone.jobs.length > 0 && (
          <div data-testid={`milestone-jobs-${milestone.id}`} className={styles.milestoneJobsSection}>
            <div className={styles.milestoneSectionHeader}><FaBriefcase size={12} color="#666" /><span className={styles.milestoneSectionLabel}>Jobs in diesem Milestone ({milestone.jobs.length})</span></div>
            <div className={styles.milestoneJobsList}>
              {milestone.jobs.map((job) => {
                const _sc = getJobStatusColors(job.status);
                const hasCL = (job.checklist_total ?? 0) > 0;
                const clProg = hasCL ? `${job.checklist_done ?? 0}/${job.checklist_total}` : null;
                const done = job.status === 'DONE', reassigning = reassigningJobId === job.id, dragging = draggedJobId === job.id;
                return (
                  <div key={job.id} data-testid={`milestone-job-${job.id}`} draggable onDragStart={(e) => p.onJobDragStart(e, job.id)} onDragEnd={p.onJobDragEnd} className={styles.milestoneJobRow} style={{ background: dragging ? '#f5f3ff' : 'white', border: dragging ? '2px dashed #7c3aed' : '1px solid #e0e3ef', opacity: dragging ? 0.6 : reassigning ? 0.5 : 1 }}>
                    <div className={styles.milestoneJobLeft}>
                      {done ? <FaCheckCircle size={14} color="#666" /> : <div className={styles.jobCircleOpen} />}
                      <div className={styles.jobInfoCol}>
                        <div className={styles.jobTitleText} style={{ color: done ? '#666' : '#021a4a', textDecoration: done ? 'line-through' : 'none' }}>{job.title}</div>
                        <div className={styles.jobMetaRow}>
                          <span className={styles.jobStatusSmall}>{getJobStatusLabel(job.status)}</span>
                          {clProg && <span className={styles.checklistText}><FaTasks size={10} style={{ marginRight: 4 }} />{clProg}</span>}
                        </div>
                      </div>
                    </div>
                    <select data-testid={`job-milestone-select-${job.id}`} value={milestone.id} onChange={(e) => p.onJobMilestoneChange(job.id, e.target.value === '' ? null : Number(e.target.value))} disabled={reassigning} onClick={(e) => e.stopPropagation()} className={styles.milestoneSelect} style={{ cursor: reassigning ? 'not-allowed' : 'pointer' }}>
                      <option value="">Nicht zugeordnet</option>
                      {milestones.map((m) => <option key={m.id} value={m.id}>{m.label} ({m.percent}%)</option>)}
                    </select>
                    <button onClick={() => p.onOpenMilestoneJobInPlantafel(job)} data-testid={`job-plantafel-link-${job.id}`} className={styles.plantafelSmallLink} title="In Plantafel öffnen"><FaExternalLinkAlt size={9} /> Plantafel</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Positions */}
        {milestone.positions && milestone.positions.length > 0 && (
          <div data-testid={`milestone-positions-${milestone.id}`} className={styles.positionsSection}>
            <div className={styles.milestoneSectionHeader}><FaFileAlt size={12} color="#666" /><span className={styles.milestoneSectionLabel}>Enthaltene Leistungen ({milestone.positions.length})</span></div>
            <div className={styles.positionsList}>
              {milestone.positions.map((pos) => (
                <div key={pos.id} data-testid={`milestone-position-${pos.id}`} className={styles.positionRow}>
                  <span className={styles.positionName}>{pos.name}</span>
                  <span className={styles.positionAmount}>{eurFmt(pos.amount)}</span>
                </div>
              ))}
            </div>
            <div className={styles.positionsTotalRow}>
              <span className={styles.positionsTotalLabel}>Summe Leistungen:</span>
              <span className={styles.positionsTotalValue}>{eurFmt(milestone.positions_total_net || 0)}</span>
            </div>
            <button data-testid={`generate-jobs-btn-${milestone.id}`} onClick={() => { p.onSetGenerateJobsMilestone(milestone); p.onSetGenerateJobsConfirmOpen(true); }} className={styles.generateJobsButton}><FaPlus size={10} /> Jobs aus Leistungen erzeugen</button>
          </div>
        )}

        {/* Tasks Toggle */}
        {milestone.tasks && milestone.tasks.length > 0 && (
          <div>
            <button onClick={() => p.onToggleMilestoneExpand(milestone.id)} className={styles.tasksToggle}>
              {isExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />} {milestone.tasks.length} Tasks anzeigen
            </button>
            {isExpanded && (
              <div className={styles.tasksList}>
                {milestone.tasks.map((task) => (
                  <div key={task.id} className={styles.taskRow} style={{ color: task.is_done ? '#10b981' : '#666' }}>
                    {task.is_done ? <FaCheckCircle size={12} color="#10b981" /> : <div className={styles.taskCircleOpen} />}
                    <span style={{ textDecoration: task.is_done ? 'line-through' : 'none' }}>{task.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
